/** @typedef {(query: string, variables?: any) => Promise<any>} GraphQLRequest */
/** @typedef {{productIds: string[], ownerId: string, compareDigest: string|null}} WishlistState */
export const API_VERSION = '2026-10';
export const MAX_ITEMS = 100;
const NS = 'app--436738031617';
const KEY = 'wishlist';
const PRODUCT_ID = /^gid:\/\/shopify\/Product\/[1-9]\d*$/;
const CUSTOMER_ID = /^gid:\/\/shopify\/Customer\/[1-9]\d*$/;
const READ = 'query Wishlist { customer { id metafield(namespace: "app--436738031617", key: "wishlist") { value compareDigest } } }';
const WRITE = 'mutation WishlistSave($values: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $values) { metafields { value compareDigest } userErrors { code field message } } }';

export class WishlistError extends Error {
  /** @param {string} code */
  constructor(code) { super(code); this.code = code; }
}
/** @param {unknown} id @returns {id is string} */
export function validProductId(id) {
  return typeof id === 'string' && PRODUCT_ID.test(id);
}
/** @param {string|null|undefined} value @returns {string[]} */
export function decodeWishlist(value) {
  if (value == null) return [];
  let parsed;
  try { parsed = JSON.parse(value); } catch { throw new WishlistError('CORRUPT_DATA'); }
  if (parsed?.version !== 1 || !Array.isArray(parsed.productIds) ||
      parsed.productIds.length > MAX_ITEMS || !parsed.productIds.every(validProductId)) {
    throw new WishlistError('CORRUPT_DATA');
  }
  return [...new Set(parsed.productIds)];
}
/** @param {string[]} ids @param {string} operation @param {string} productId */
export function applyOperation(ids, operation, productId) {
  if (!validProductId(productId)) throw new WishlistError('INVALID_PRODUCT');
  if (operation === 'remove') return ids.filter(id => id !== productId);
  if (operation !== 'add') throw new WishlistError('INVALID_OPERATION');
  if (ids.includes(productId)) return [...ids];
  if (ids.length >= MAX_ITEMS) throw new WishlistError('LIMIT_REACHED');
  return [...ids, productId];
}
/** @type {GraphQLRequest} */
export async function accountRequest(query, variables = {}) {
  const response = await fetch('shopify:customer-account/api/' + API_VERSION + '/graphql.json', {
    method: 'POST', headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({query, variables}),
  });
  if (!response.ok) throw new WishlistError('REQUEST_FAILED');
  const result = await response.json();
  if (result.errors?.length || !result.data) throw new WishlistError('REQUEST_FAILED');
  return result.data;
}
/** @param {GraphQLRequest} request */
export function createWishlistClient(request = accountRequest) {
  /** @type {string|null} */
  let ownerId = null;
  /** @type {Promise<unknown>} */
  let pending = Promise.resolve();
  /** @returns {Promise<WishlistState>} */
  async function read() {
    const data = await request(READ);
    const customer = data?.customer;
    if (!customer || typeof customer.id !== 'string' || !CUSTOMER_ID.test(customer.id)) throw new WishlistError('SIGN_IN_REQUIRED');
    if (ownerId && ownerId !== customer.id) throw new WishlistError('ACCOUNT_CHANGED');
    ownerId = customer.id;
    const field = customer.metafield;
    if (field && typeof field.compareDigest !== 'string') throw new WishlistError('REQUEST_FAILED');
    return {productIds: decodeWishlist(field?.value), ownerId: customer.id,
      compareDigest: field ? field.compareDigest : null};
  }
  /** @param {string} operation @param {string} productId */
  async function commit(operation, productId) {
    if (!validProductId(productId)) throw new WishlistError('INVALID_PRODUCT');
    for (let attempt = 0; attempt < 3; attempt++) {
      const current = await read();
      const next = applyOperation(current.productIds, operation, productId);
      if (JSON.stringify(next) === JSON.stringify(current.productIds)) return current;
      const data = await request(WRITE, {values: [{namespace: NS, key: KEY, type: 'json',
        ownerId: current.ownerId, compareDigest: current.compareDigest,
        value: JSON.stringify({version: 1, productIds: next})}]});
      const payload = data?.metafieldsSet;
      if (!payload || !Array.isArray(payload.userErrors)) throw new WishlistError('REQUEST_FAILED');
      if (payload.userErrors.length) {
        if (payload.userErrors.every((/** @type {{code: string}} */ error) => ['STALE_OBJECT', 'INVALID_COMPARE_DIGEST'].includes(error.code))) continue;
        throw new WishlistError('SAVE_FAILED');
      }
      const saved = payload.metafields?.[0];
      if (!saved || typeof saved.compareDigest !== 'string') throw new WishlistError('REQUEST_FAILED');
      return {productIds: decodeWishlist(saved.value), ownerId: current.ownerId, compareDigest: saved.compareDigest};
    }
    throw new WishlistError('CONFLICT');
  }
  /** @param {string} operation @param {string} productId */
  function change(operation, productId) {
    const task = pending.then(() => commit(operation, productId));
    pending = task.catch(() => {});
    return task;
  }
  return {read, add: (/** @type {string} */ id) => change('add', id), remove: (/** @type {string} */ id) => change('remove', id)};
}

// Public browser client. No Admin token or client secret belongs in theme code.
export const CLIENT_ID = '798dede0-0f96-4f19-8907-b8d1e91005e0';
export const REDIRECT_URI = 'https://bondtale.com/?btl_wishlist_callback=1';
const SESSION_KEY = 'btl:wishlist:session:v1';
export function base64url(bytes) {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export async function pkceChallenge(verifier, cryptoApi = globalThis.crypto) {
  return base64url(new Uint8Array(await cryptoApi.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}
export function randomKey(cryptoApi = globalThis.crypto) {
  return base64url(cryptoApi.getRandomValues(new Uint8Array(32)));
}
export function trustedEndpoint(value) {
  const url = new URL(value);
  if (url.protocol !== 'https:' || url.username || url.password ||
      !(url.hostname === 'shopify.com' || url.hostname === 'bondtale.com' ||
        url.hostname.endsWith('.bondtale.com') || url.hostname === '10k066-0w.myshopify.com')) {
    throw new WishlistError('CONFIGURATION_ERROR');
  }
  return url.href;
}
export function validateCallback(data, transaction, now = Date.now()) {
  if (!transaction || typeof data?.state !== 'string' || data.state !== transaction.state ||
      !Number.isFinite(transaction.createdAt) || now - transaction.createdAt > 600000 || now < transaction.createdAt) throw new WishlistError('INVALID_LOGIN');
  if (data.error) throw new WishlistError('SIGN_IN_REQUIRED');
  if (typeof data.code !== 'string' || !data.code || data.code.length > 4096) throw new WishlistError('INVALID_LOGIN');
  return data.code;
}
export function tokenSession(data, now = Date.now(), previous = null) {
  if (typeof data?.access_token !== 'string' || !data.access_token ||
      !Number.isFinite(data.expires_in) || data.expires_in <= 0 || data.expires_in > 86400) {
    throw new WishlistError('SIGN_IN_REQUIRED');
  }
  return {clientId: CLIENT_ID, accessToken: data.access_token,
    refreshToken: typeof data.refresh_token === 'string' ? data.refresh_token : previous?.refreshToken,
    expiresAt: now + data.expires_in * 1000, ownerId: previous?.ownerId || null};
}
export function validSession(value) {
  return value?.clientId === CLIENT_ID && typeof value.accessToken === 'string' && value.accessToken.length > 0 &&
    Number.isFinite(value.expiresAt) && (!value.ownerId || /^gid:\/\/shopify\/Customer\/[1-9]\d*$/.test(value.ownerId));
}
export function belongsToCustomer(value, customerId) {
  return validSession(value) && typeof customerId === 'string' && /^[1-9]\d*$/.test(customerId) && value.ownerId === 'gid://shopify/Customer/' + customerId;
}
export function authErrorMessage(error) {
  const messages = {
    LIMIT_REACHED: 'Your wishlist is full. Remove an item before saving another.',
    CONFLICT: 'Your wishlist changed on another device. Please try again.',
    ACCOUNT_CHANGED: 'Your account changed. Please sign in again.',
    SIGN_IN_REQUIRED: 'Please sign in to save your favourites.',
    LOGIN_CANCELLED: 'Sign-in cancelled. Your wishlist has not changed.',
    INVALID_LOGIN: 'Sign-in could not be verified. Please try again.',
    POPUP_BLOCKED: 'Please allow the Shopify sign-in window, then click the heart again.',
    LOGIN_TIMED_OUT: 'Sign-in timed out. Please click the heart to try again.',
    SIGN_IN_RETRY: 'Please click the heart again to reconnect your Shopify account.',
    CORRUPT_DATA: 'Your wishlist could not be loaded. Please try again later.',
  };
  return messages[error?.code] || 'Could not update your wishlist. Please try again.';
}


export function safeReturnPath(value, origin = 'https://bondtale.com') {
  if (typeof value !== 'string' || value.length > 4096 || !value.startsWith('/') || value.startsWith('//')) return '/';
  try {
    const url = new URL(value, origin);
    if (url.origin !== origin || url.searchParams.has('btl_wishlist_callback')) return '/';
    return url.pathname + url.search + url.hash;
  } catch { return '/'; }
}
const LOGIN_KEY = 'btl:wishlist:redirect:v2';
const CALLBACK_KEY = 'btl:wishlist:callback:v2';
const NOTICE_KEY = 'btl:wishlist:notice:v2';
const SNAPSHOT_KEY = 'btl:wishlist:state:v1';
export function cachedWishlist(snapshot, session, customerId) {
  if (!belongsToCustomer(session, customerId) || snapshot?.version !== 1 || snapshot.ownerId !== session.ownerId) return [];
  try { return decodeWishlist(JSON.stringify({version: 1, productIds: snapshot.productIds})); } catch { return []; }
}

// Liquid supplies only the current signed-in customer's server wishlist.
// Reading it does not require this tab to have a Customer Account API token.
export function storefrontWishlist(config) {
  if (typeof config?.customerId !== 'string' || !/^[1-9]\d*$/.test(config.customerId) ||
      !Object.prototype.hasOwnProperty.call(config, 'wishlist')) return null;
  if (config.wishlist === null) return [];
  try { return decodeWishlist(JSON.stringify(config.wishlist)); } catch { return null; }
}

async function startStorefrontWishlist() {
  if (window.btlDirectWishlistReady) return;
  window.btlDirectWishlistReady = true;
  let session = null, configPromise = null, refreshPromise = null, redirecting = false;
  const renderedIds = storefrontWishlist(window.btlWishlistConfig);
  let ids = new Set(renderedIds || []), client = null, revision = 0, confirmedAt = 0, refreshTimer;
  const pendingProducts = new Set();
  let activeSignIn = null;
  try {
    const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    const liquidId = window.btlWishlistConfig?.customerId;
    if (belongsToCustomer(stored, liquidId)) {
      session = stored;
      const snapshot = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || 'null');
      ids = new Set(renderedIds ?? cachedWishlist(snapshot, stored, liquidId));
      if (Number.isFinite(snapshot?.confirmedAt) && snapshot.confirmedAt <= Date.now()) confirmedAt = snapshot.confirmedAt;
    } else { sessionStorage.removeItem(SESSION_KEY); sessionStorage.removeItem(SNAPSHOT_KEY); }
  } catch { /* Storage may be unavailable; the current page still works. */ }
  function persist() {
    try {
      if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      else sessionStorage.removeItem(SESSION_KEY);
    } catch { /* Do not fall back to persistent browser storage. */ }
  }
  function clearSession() {
    session = null; client = null; ids = new Set(); revision++; confirmedAt = 0; clearTimeout(refreshTimer);
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch {}
    persist(); paint();
  }
  async function discover() {
    if (!configPromise) configPromise = Promise.all([
      fetch('/.well-known/openid-configuration', {credentials: 'omit', cache: 'no-store'}).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
      fetch('/.well-known/customer-account-api', {credentials: 'omit', cache: 'no-store'}).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
    ]).then(([auth, api]) => ({authorize: trustedEndpoint(auth.authorization_endpoint),
      token: trustedEndpoint(auth.token_endpoint), graphql: trustedEndpoint(api.graphql_api)}))
      .catch(() => { configPromise = null; throw new WishlistError('CONFIGURATION_ERROR'); });
    return configPromise;
  }
  async function exchange(body) {
    const config = await discover();
    const response = await fetch(config.token, {method: 'POST', credentials: 'omit',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'}, body});
    if (!response.ok) throw new WishlistError('SIGN_IN_REQUIRED');
    session = tokenSession(await response.json(), Date.now(), session);
    persist();
  }
  async function accessToken(forceRefresh = false) {
    if (!session) throw new WishlistError('SIGN_IN_REQUIRED');
    if (!forceRefresh && session.expiresAt > Date.now() + 30000) return session.accessToken;
    if (!session.refreshToken) { clearSession(); throw new WishlistError('SIGN_IN_REQUIRED'); }
    if (!refreshPromise) refreshPromise = exchange(new URLSearchParams({grant_type: 'refresh_token',
      client_id: CLIENT_ID, refresh_token: session.refreshToken})).catch(error => { clearSession(); throw error; })
      .finally(() => { refreshPromise = null; });
    await refreshPromise;
    return session.accessToken;
  }
  async function request(query, variables = {}, retryUnauthorized = false) {
    const [config, token] = await Promise.all([discover(), accessToken(retryUnauthorized)]);
    const response = await fetch(config.graphql, {method: 'POST', credentials: 'omit', cache: 'no-store',
      headers: {'Content-Type': 'application/json', Authorization: token}, body: JSON.stringify({query, variables})});
    if (response.status === 401) { if (!retryUnauthorized && session?.refreshToken) return request(query, variables, true); clearSession(); throw new WishlistError('SIGN_IN_REQUIRED'); }
    if (!response.ok) throw new WishlistError('REQUEST_FAILED');
    const result = await response.json();
    if (result.errors?.length || !result.data) throw new WishlistError('REQUEST_FAILED');
    return result.data;
  }
  function storageClient() { return client || (client = createWishlistClient(request)); }
  function acceptState(state, confirmed = false) {
    const liquidCustomer = window.btlWishlistConfig?.customerId;
    if ((session?.ownerId && session.ownerId !== state.ownerId) ||
        (liquidCustomer && 'gid://shopify/Customer/' + liquidCustomer !== state.ownerId)) {
      clearSession(); throw new WishlistError('ACCOUNT_CHANGED');
    }
    session.ownerId = state.ownerId;
    if (confirmed) confirmedAt = Date.now();
    persist(); ids = new Set(state.productIds);
    try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify({version: 1, ownerId: state.ownerId, productIds: state.productIds, confirmedAt})); } catch {}
    paint();
  }
  function paint() {
    document.querySelectorAll('[data-btl-wishlist-product]').forEach(button => {
      const id = button.dataset.btlWishlistProduct, saved = ids.has(id), busy = pendingProducts.has(id);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-busy', String(busy));
      button.classList.toggle('is-saved', saved);
      button.classList.toggle('is-busy', busy);
      button.setAttribute('aria-label', (saved ? 'Remove ' : 'Save ') + (button.dataset.productTitle || 'product') +
        (saved ? ' from wishlist' : ' to wishlist'));
      const label = button.querySelector('[data-wishlist-label]');
      if (label) { const text = busy ? (redirecting ? 'Signing in…' : 'Saving…') : saved ? 'Saved to wishlist' : 'Save to wishlist'; if (label.textContent !== text) label.textContent = text; }
    });
  }

  function relayPopupAuth(transaction, result) {
    const payload = {type: 'btl:wishlist:authorization', state: transaction.state, ...result};
    let channel;
    try { if (typeof BroadcastChannel === 'function') channel = new BroadcastChannel('btl:wishlist:auth:' + transaction.state); } catch {}
    const finish = () => { channel?.close(); try { window.close(); } catch {} };
    const acknowledged = data => data?.type === 'btl:wishlist:ack' && data.state === transaction.state;
    if (channel) channel.onmessage = event => { if (acknowledged(event.data)) finish(); };
    window.addEventListener('message', event => {
      if (event.origin === location.origin && event.source === window.opener && acknowledged(event.data)) finish();
    });
    try { sessionStorage.removeItem(LOGIN_KEY); sessionStorage.removeItem(CALLBACK_KEY); } catch {}
    try { window.opener?.postMessage(payload, location.origin); } catch {}
    channel?.postMessage(payload);
    document.title = 'Completing wishlist sign-in';
    const panel = document.createElement('main');
    panel.style.cssText = 'min-height:100vh;display:grid;place-items:center;padding:24px;font:16px/1.5 system-ui;color:#30343a;background:#fff;';
    panel.textContent = 'Completing sign-in. Your wishlist will update on the page you left open.';
    document.body.replaceChildren(panel);
    window.setTimeout(finish, 3000);
  }

  const callback = new URL(location.href).searchParams;
  if (callback.get('btl_wishlist_callback') === '1') {
    let returnPath = '/', transaction = null;
    document.title = 'Completing sign-in';
    try {
      transaction = JSON.parse(sessionStorage.getItem(LOGIN_KEY) || 'null');
      if (!transaction || !validProductId(transaction.productId) || !/^[A-Za-z0-9_-]{43}$/.test(transaction.verifier)) throw new WishlistError('INVALID_LOGIN');
      returnPath = safeReturnPath(transaction.returnPath, location.origin);
      let message = {state: callback.get('state'), code: callback.get('code'), error: callback.get('error')};
      history.replaceState(null, '', '/?btl_wishlist_callback=1');
      if (!message.state) message = JSON.parse(sessionStorage.getItem(CALLBACK_KEY) || 'null');
      const code = validateCallback(message, transaction);
      if (!window.btlWishlistConfig?.customerId) {
        if (message.storeLoginAttempted) throw new WishlistError('SIGN_IN_REQUIRED');
        message.storeLoginAttempted = true;
        sessionStorage.setItem(CALLBACK_KEY, JSON.stringify(message));
        location.replace('/customer_authentication/login?return_to=' + encodeURIComponent('/?btl_wishlist_callback=1'));
        return;
      }
      if (transaction.mode === 'popup') { relayPopupAuth(transaction, {code}); return; }
      await exchange(new URLSearchParams({grant_type: 'authorization_code', client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI, code, code_verifier: transaction.verifier}));
      client = null;
      acceptState(await storageClient().read());
      acceptState(await storageClient().add(transaction.productId), true);
      try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify({message: 'Saved to wishlist', saved: true, productId: transaction.productId, buttonIndex: transaction.buttonIndex, scrollY: transaction.scrollY, viewportTop: transaction.viewportTop})); } catch {}
    } catch (error) {
      if (transaction?.mode === 'popup') { relayPopupAuth(transaction, {error: error.code || 'INVALID_LOGIN'}); return; }
      clearSession();
      try { sessionStorage.setItem(NOTICE_KEY, JSON.stringify({message: authErrorMessage(error), saved: false, productId: transaction?.productId, buttonIndex: transaction?.buttonIndex, scrollY: transaction?.scrollY, viewportTop: transaction?.viewportTop})); } catch {}
    }
    try { sessionStorage.removeItem(LOGIN_KEY); sessionStorage.removeItem(CALLBACK_KEY); } catch {}
    location.replace(returnPath);
    return;
  }
  const toast = document.createElement('div');
  toast.className = 'btl-wishlist-toast'; toast.setAttribute('role', 'status'); toast.setAttribute('aria-live', 'polite');
  document.body.append(toast);
  let toastTimer;
  function notify(message, action = null) {
    toast.textContent = message; toast.classList.add('is-visible'); clearTimeout(toastTimer);
    if (action) {
      const button = document.createElement('button');
      button.type = 'button'; button.textContent = action.label;
      button.style.cssText = 'margin-left:12px;padding:2px 4px;border:0;background:transparent;color:inherit;font:inherit;text-decoration:underline;cursor:pointer;pointer-events:auto;';
      button.addEventListener('click', action.run, {once: true}); toast.append(button);
    } else toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 5000);
  }
  function celebrate(button) {
    if (!button || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    const heart = button.querySelector('svg');
    if (!heart?.animate) return;
    heart.animate([{transform: 'scale(1)'}, {transform: 'scale(1.32)', offset: .35},
      {transform: 'scale(.94)', offset: .7}, {transform: 'scale(1)'}], {duration: 460, easing: 'cubic-bezier(.2,.8,.2,1)'});
    const ring = document.createElement('span'); ring.className = 'btl-wishlist-burst';
    ring.setAttribute('aria-hidden', 'true'); button.append(ring);
    const animation = ring.animate([{opacity: .55, transform: 'translate(-50%,-50%) scale(.6)'},
      {opacity: 0, transform: 'translate(-50%,-50%) scale(1.7)'}], {duration: 520, easing: 'ease-out'});
    animation.onfinish = () => ring.remove();
  }
  function matchingButtons(productId) {
    return Array.from(document.querySelectorAll('[data-btl-wishlist-product]')).filter(button => button.dataset.btlWishlistProduct === productId);
  }
  function restoreReturn(notice) {
    if (!validProductId(notice?.productId)) return;
    let userMoved = false, animated = false;
    const cancel = () => { userMoved = true; };
    for (const event of ['pointerdown', 'wheel', 'touchstart']) window.addEventListener(event, cancel, {once: true, passive: true});
    const restore = () => {
      if (userMoved) return;
      const buttons = matchingButtons(notice.productId);
      const index = Number.isInteger(notice.buttonIndex) && notice.buttonIndex >= 0 ? notice.buttonIndex : 0;
      const button = buttons[index] || buttons[0];
      if (!button) return;
      const top = Number.isFinite(notice.viewportTop) ? Math.max(96, Math.min(window.innerHeight - 96, notice.viewportTop)) : 160;
      window.scrollTo({top: Math.max(0, window.scrollY + button.getBoundingClientRect().top - top), behavior: 'instant'});
      button.focus({preventScroll: true});
      if (!animated && notice.saved && ids.has(notice.productId)) { animated = true; celebrate(button); }
    };
    requestAnimationFrame(() => requestAnimationFrame(restore));
    if (document.readyState !== 'complete') window.addEventListener('load', restore, {once: true});
  }
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY); sessionStorage.removeItem(NOTICE_KEY);
    if (raw) {
      let notice; try { notice = JSON.parse(raw); } catch { notice = {message: raw}; }
      if (typeof notice?.message === 'string') notify(notice.message.slice(0, 200));
      restoreReturn(notice);
    }
  } catch {}
  async function beginSignIn(productId, trigger, action = 'add') {
    if (redirecting || activeSignIn) return;
    redirecting = true; pendingProducts.add(productId); paint();
    let attempt;
    try {
      const state = randomKey(), verifier = randomKey();
      const transaction = {state, verifier, productId, action, mode: 'popup', createdAt: Date.now(),
        returnPath: safeReturnPath(location.pathname + location.search + location.hash, location.origin)};
      sessionStorage.setItem(LOGIN_KEY, JSON.stringify(transaction));
      sessionStorage.removeItem(CALLBACK_KEY);
      const popup = window.open('about:blank', 'btlWishlistAuth_' + state, 'popup=yes,width=520,height=720');
      if (!popup) throw new WishlistError('POPUP_BLOCKED');
      attempt = {transaction, popup, completing: false}; activeSignIn = attempt;
      let channel;
      try { if (typeof BroadcastChannel === 'function') channel = new BroadcastChannel('btl:wishlist:auth:' + state); } catch {}
      const cleanup = () => {
        clearTimeout(attempt.timer); window.removeEventListener('message', onMessage); channel?.close();
        try { popup.close(); } catch {}
        try { sessionStorage.removeItem(LOGIN_KEY); sessionStorage.removeItem(CALLBACK_KEY); } catch {}
        if (activeSignIn === attempt) activeSignIn = null;
      };
      attempt.cancel = code => {
        if (activeSignIn !== attempt || attempt.completing) return;
        cleanup(); redirecting = false; pendingProducts.delete(productId); paint();
        if (code) notify(authErrorMessage(new WishlistError(code)));
      };
      const complete = async data => {
        if (activeSignIn !== attempt || attempt.completing || data?.type !== 'btl:wishlist:authorization' || data.state !== state) return;
        attempt.completing = true; clearTimeout(attempt.timer);
        const ack = {type: 'btl:wishlist:ack', state};
        try { channel?.postMessage(ack); popup.postMessage(ack, location.origin); } catch {}
        redirecting = false; paint(); notify('Saving to wishlist…');
        try {
          const code = validateCallback(data, transaction);
          await exchange(new URLSearchParams({grant_type: 'authorization_code', client_id: CLIENT_ID,
            redirect_uri: REDIRECT_URI, code, code_verifier: verifier}));
          client = null; acceptState(await storageClient().read());
          const removing = action === 'remove';
          acceptState(await storageClient()[removing ? 'remove' : 'add'](productId), true);
          notify(removing ? 'Removed from wishlist' : 'Saved to wishlist');
          if (!removing) celebrate(trigger);
        } catch (error) {
          if (['SIGN_IN_REQUIRED', 'ACCOUNT_CHANGED', 'INVALID_LOGIN'].includes(error.code)) clearSession();
          notify(authErrorMessage(error));
        } finally {
          cleanup(); revision++; pendingProducts.delete(productId); paint();
        }
      };
      const onMessage = event => {
        if (event.origin === location.origin && event.source === popup) complete(event.data);
      };
      window.addEventListener('message', onMessage);
      if (channel) channel.onmessage = event => complete(event.data);
      attempt.timer = setTimeout(() => attempt.cancel('LOGIN_TIMED_OUT'), 240000);
      notify('Complete Shopify sign-in in the opened window.', {label: 'Cancel', run: () => attempt.cancel('LOGIN_CANCELLED')});
      try { popup.document.title = 'Shopify wishlist sign-in'; popup.document.body.textContent = 'Connecting securely to Shopify…'; } catch {}
      const [config, challenge] = await Promise.all([discover(), pkceChallenge(verifier)]);
      if (activeSignIn !== attempt) return;
      if (popup.closed) { attempt.cancel('LOGIN_CANCELLED'); return; }
      const authUrl = new URL(config.authorize);
      authUrl.search = new URLSearchParams({client_id: CLIENT_ID, redirect_uri: REDIRECT_URI,
        response_type: 'code', scope: 'openid email customer-account-api:full', state,
        code_challenge: challenge, code_challenge_method: 'S256'}).toString();
      popup.location.replace(authUrl.href);
      try { popup.focus(); } catch {}
    } catch (error) {
      if (attempt?.cancel) attempt.cancel(error.code || 'CONFIGURATION_ERROR');
      else {
        redirecting = false; pendingProducts.delete(productId); paint();
        try { sessionStorage.removeItem(LOGIN_KEY); } catch {}
        notify(authErrorMessage(error));
      }
    }
  }
  async function change(productId, trigger, addOnly = false) {
    if (pendingProducts.has(productId)) return;
    if (!session || (session.expiresAt <= Date.now() + 30000 && !session.refreshToken)) {
      const action = !addOnly && ids.has(productId) ? 'remove' : 'add';
      if (session) clearSession();
      beginSignIn(productId, trigger, action); return;
    }
    const removing = !addOnly && ids.has(productId);
    revision++; pendingProducts.add(productId); paint();
    try {
      
      const next = await storageClient()[removing ? 'remove' : 'add'](productId); acceptState(next, true);
      notify(removing ? 'Removed from wishlist' : 'Saved to wishlist');
      if (!removing) celebrate(trigger);
    } catch (error) {
      if (['SIGN_IN_REQUIRED', 'ACCOUNT_CHANGED'].includes(error.code)) {
        clearSession(); pendingProducts.delete(productId);
        notify(authErrorMessage(new WishlistError('SIGN_IN_RETRY')), {label: 'Sign in',
          run: () => beginSignIn(productId, trigger, removing ? 'remove' : 'add')});
      }
      else notify(authErrorMessage(error));
    } finally { revision++; pendingProducts.delete(productId); paint(); }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-btl-wishlist-product]');
    if (button && validProductId(button.dataset.btlWishlistProduct)) {
      event.preventDefault(); event.stopImmediatePropagation(); change(button.dataset.btlWishlistProduct, button); return;
    }
    const link = event.target.closest('a[href]');
    if (link && /\/logout(?:[/?#]|$)/.test(new URL(link.href, location.origin).pathname)) clearSession();
  }, true);
  const observer = new MutationObserver(paint);
  observer.observe(document.body, {childList: true, subtree: true});
  // Only observe added nodes: paint changes attributes, not the observed mutation types.
  let reading = false;
  async function reloadState() {
    if (!session || reading) return;
    const settleDelay = confirmedAt + 5000 - Date.now();
    if (settleDelay > 0) { clearTimeout(refreshTimer); refreshTimer = setTimeout(reloadState, settleDelay + 20); return; }
    reading = true; const readRevision = revision;
    try { const state = await storageClient().read(); if (!pendingProducts.size && revision === readRevision) acceptState(state); }
    catch (error) { if (revision === readRevision && ['SIGN_IN_REQUIRED', 'ACCOUNT_CHANGED'].includes(error.code)) clearSession(); }
    finally { reading = false; }
  }
  window.addEventListener('pagehide', () => activeSignIn?.cancel(null));
  window.addEventListener('focus', reloadState);
  window.addEventListener('pageshow', () => {
    if (redirecting) { redirecting = false; pendingProducts.clear(); paint(); }
    reloadState();
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) reloadState(); });
  paint(); reloadState();
}
if (typeof document !== 'undefined') {
  const boot = () => startStorefrontWishlist().catch(() => {});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();
}
